import React, { useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  Avatar,
  Badge,
  Spinner,
} from '@chakra-ui/react';
import { CalendarBlank, MapPin, Columns } from 'phosphor-react';
import moment from 'moment';
import 'moment/locale/pt-br';
import { useNavigate } from 'react-router-dom';
import SecaoClienteLayout from '../layout';
import { api } from '../../../services/api';
import { formatCurrency } from '../../../utils/FormatFunctions';

interface ClienteEvento {
  id: number;
  nome: string;
  artista?: string;
  dt_evento?: string;
  local?: string;
  cidade?: string;
  uf?: string;
  valor_orcamento: number;
  status: string;
  total_contratos: number;
  total_assinados: number;
  kanban_projeto_id?: number;
}

export default function SecaoClienteEventos() {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState<ClienteEvento[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const clienteData = JSON.parse(localStorage.getItem('cliente_data') || '{}');

  React.useEffect(() => {
    const fetchEventos = async () => {
      try {
        const token = localStorage.getItem('cliente_access_token');
        const response = await api.get('/api/v1/secao-cliente/eventos', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEventos(response.data?.data ?? []);
      } catch {
        setEventos([]);
      } finally {
        setIsLoading(false);
      }
    };
    if (clienteData.id) fetchEventos();
    else setIsLoading(false);
  }, [clienteData.id]);

  // formatCurrency importado de FormatFunctions

  return (
    <SecaoClienteLayout>
      <Box p={6}>
        <Heading size="lg" mb={2} color="gray.800">
          Eventos
        </Heading>
        <Text fontSize="sm" color="gray.500" mb={6}>
          Acompanhe os eventos das suas empresas
        </Text>

        {isLoading && (
          <Flex justify="center" py={10}>
            <Spinner size="lg" color="blue.400" />
          </Flex>
        )}

        {!isLoading && eventos.length === 0 && (
          <Flex justify="center" py={10}>
            <Text color="gray.400">Nenhum evento encontrado</Text>
          </Flex>
        )}

        <VStack spacing={3} align="stretch">
          {eventos.map(evento => (
            <Card
              key={evento.id}
              cursor="pointer"
              onClick={() => navigate(`/secao-cliente/eventos/${evento.id}`)}
              _hover={{ shadow: 'md', borderColor: 'blue.200' }}
              transition="all 0.15s"
              border="1px solid"
              borderColor="gray.200"
            >
              <CardBody py={4}>
                <Flex justify="space-between" align="center">
                  <HStack spacing={4}>
                    <Avatar size="md" name={evento.nome} bg="blue.500" color="white" />
                    <Box>
                      <Text fontWeight="700" fontSize="md" color="gray.800">
                        {evento.nome}
                      </Text>
                      <HStack spacing={3} mt={1} fontSize="xs" color="gray.500">
                        {evento.artista && <Text>{evento.artista}</Text>}
                        {evento.dt_evento && (
                          <HStack spacing={1}>
                            <CalendarBlank size={12} />
                            <Text>{moment(evento.dt_evento).format('DD/MM/YYYY')}</Text>
                          </HStack>
                        )}
                        {evento.local && (
                          <HStack spacing={1}>
                            <MapPin size={12} />
                            <Text>{evento.local}</Text>
                          </HStack>
                        )}
                      </HStack>
                    </Box>
                  </HStack>
                  <VStack spacing={1} align="flex-end">
                    <Text fontWeight="600" color="gray.700">
                      {formatCurrency(evento.valor_orcamento)}
                    </Text>
                    <HStack spacing={2}>
                      <Badge colorScheme="blue" fontSize="2xs">
                        {evento.total_contratos} contratos
                      </Badge>
                      <Badge colorScheme="green" fontSize="2xs">
                        {evento.total_assinados} assinados
                      </Badge>
                      {evento.kanban_projeto_id && (
                        <Badge colorScheme="purple" fontSize="2xs">
                          <Columns size={10} style={{ display: 'inline', marginRight: 3 }} />
                          Kanban
                        </Badge>
                      )}
                    </HStack>
                  </VStack>
                </Flex>
              </CardBody>
            </Card>
          ))}
        </VStack>
      </Box>
    </SecaoClienteLayout>
  );
}
